import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

// Shown when someone taps a forum magic-link that's expired, already
// been used, or is missing/invalid — either straight from the email
// (backend redirects here with ?error=1) or via the legacy SPA token
// flow below (axios call to /forum/auth/magic/verify fails).
export default function ForumMagic() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [expired, setExpired] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState("");

  useEffect(() => {
    if (params.get("error")) {
      setExpired(true);
      setChecking(false);
      return;
    }
    const token = params.get("token") || "";
    if (!token) {
      setExpired(true);
      setChecking(false);
      return;
    }
    api
      .get(`/forum/auth/magic/verify?token=${encodeURIComponent(token)}`)
      .then(() => navigate("/thelastpost", { replace: true }))
      .catch(() => {
        setExpired(true);
        setChecking(false);
      });
  }, [params, navigate]);

  const requestFresh = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setSendError("");
    try {
      await api.post("/forum/auth/magic/request", { email: email.trim() });
      setSent(true);
    } catch (e2) {
      setSendError(formatApiErrorDetail(e2?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]" data-testid="page-forum-magic">
      <Header />
      <main className="mx-auto max-w-3xl px-5 pt-10 pb-16 sm:px-8 sm:pt-14">
        {checking ? (
          <div
            className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center"
            data-testid="forum-magic-checking"
          >
            <Loader2 className="h-6 w-6 animate-spin text-brand-accent" />
            <p className="text-sm text-[var(--brand-body)]">Signing you in…</p>
          </div>
        ) : null}

        {!checking && expired ? (
          <div
            className="mx-auto max-w-sm rounded-md border border-black/10 bg-white p-6 text-center sm:p-8"
            data-testid="forum-magic-expired"
          >
            <h1 className="font-display text-xl font-black" data-testid="forum-magic-title">
              This link has expired or already been used
            </h1>
            <p className="mt-2 text-sm text-[var(--brand-body)]">
              Magic sign-in links only work once and expire after 15 minutes.
              Pop your email in below and we&apos;ll send you a fresh one.
            </p>

            {sent ? (
              <p className="mt-5 text-sm text-[var(--brand-body)]" data-testid="forum-magic-sent">
                If that email&apos;s registered, a fresh link is on its way. It
                expires in 15 minutes — check your inbox.
              </p>
            ) : (
              <form
                onSubmit={requestFresh}
                className="mt-5 space-y-3 text-left"
                data-testid="forum-magic-resend-form"
              >
                <label className="block">
                  <span className="label-kicker text-[var(--brand-body)]">Email</span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="forum-magic-email"
                    className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                  />
                </label>
                <button
                  type="submit"
                  disabled={sending || !email.trim()}
                  data-testid="forum-magic-send"
                  className="w-full rounded-md bg-[#0A0F17] px-4 py-3 font-display text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send me a new link"}
                </button>
                {sendError ? (
                  <p className="text-xs text-red-600" data-testid="forum-magic-send-error">
                    {sendError}
                  </p>
                ) : null}
              </form>
            )}

            <a
              href="/thelastpost"
              className="mt-6 inline-block text-sm underline underline-offset-2"
              data-testid="forum-magic-back-link"
            >
              Back to The Last Post
            </a>
          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
